import axiosServices from 'utils/axios';
import useSWR, { mutate } from 'swr';
import { useMemo } from 'react';

// ==============================|| TENANTS API ||============================== //

export const DEFAULT_MAX_FILE_AGE_DAYS = 7;
export const MIN_FILE_AGE_DAYS = 1;
export const MAX_FILE_AGE_DAYS = 30;
export const DEFAULT_NUM_PARALLEL_EXPORTS = 5;
export const MIN_PARALLEL_EXPORTS = 1;
export const MAX_PARALLEL_EXPORTS = 100;

// Tax values are server-owned defaults for new tenants; they round-trip unchanged when present.
export interface TenantSettings {
  taxCode?: string;
  taxRate?: number;
  maxFileAgeDays: number;
  numParallelExports: number;
}

export interface Tenant {
  tenantId: number;
  tenantName: string;
  statusId: number | null;
  statusName: string | null;
  canUse: boolean;
  code: string | null;
  shardId: string | null;
  shardName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  suburb: string | null;
  cityId: number | null;
  city: string | null;
  countryId: number | null;
  region: string | null;
  country: string | null;
  billingContact: {
    name: string | null;
    email: string | null;
  };
  technicalContact: {
    name: string | null;
    email: string | null;
  };
  settings: TenantSettings;
}

export interface UpsertTenantRequest {
  tenantName: string;
  statusId: number | null;
  statusName: string | null;
  code: string | null;
  shardId: string | null;
  shardName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  suburb: string | null;
  cityId: number | null;
  city: string | null;
  countryId: number | null;
  region: string | null;
  country: string | null;
  billingContact: {
    name: string | null;
    email: string | null;
  };
  technicalContact: {
    name: string | null;
    email: string | null;
  };
  settings: TenantSettings;
}

const endpoints = {
  key: 'api/tenants'
};

type TenantApiShape = Record<string, unknown>;

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};
const asNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
  return 0;
};
const asNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
  return null;
};
const asBoolean = (value: unknown): boolean => value === true || value === 'true';
const asNullableIdString = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

function normalizeSettings(value: unknown): TenantSettings {
  const raw = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const maxFileAgeDays = asNullableNumber(raw.maxFileAgeDays);
  const numParallelExports = asNullableNumber(raw.numParallelExports);
  return {
    ...(typeof raw.taxCode === 'string' ? { taxCode: raw.taxCode } : {}),
    ...(typeof raw.taxRate === 'number' ? { taxRate: raw.taxRate } : {}),
    maxFileAgeDays: maxFileAgeDays ?? DEFAULT_MAX_FILE_AGE_DAYS,
    numParallelExports: numParallelExports ?? DEFAULT_NUM_PARALLEL_EXPORTS
  };
}

function normalizeTenant(raw: TenantApiShape): Tenant {
  const statusId = asNullableNumber(raw.statusId ?? raw.StatusId);
  const shardId = asNullableIdString(raw.shardId ?? raw.ShardId);
  const city = asNullableString(raw.city ?? raw.cityName ?? raw.CityName);
  const region = asNullableString(raw.region ?? raw.state ?? raw.State ?? raw.stateName ?? raw.StateName);
  const building = asNullableString(raw.building ?? raw.Building);
  const street = asNullableString(raw.street ?? raw.Street);
  const explicitAddressLine1 = asNullableString(raw.addressLine1 ?? raw.address1);
  const explicitAddressLine2 = asNullableString(raw.addressLine2 ?? raw.address2);
  const addressLine1 = explicitAddressLine1 ?? building ?? (building ? null : street);
  const addressLine2 = explicitAddressLine2 ?? (building ? street : null);
  const normalizedRegion = city && region && city.localeCompare(region, undefined, { sensitivity: 'accent' }) === 0 ? null : region;
  const rawBillingContact = (raw.billingContact ?? raw.BillingContact) as Record<string, unknown> | null | undefined;
  const rawTechnicalContact = (raw.technicalContact ?? raw.TechnicalContact) as Record<string, unknown> | null | undefined;
  const billingContactName = asNullableString(rawBillingContact?.name);
  const billingContactEmail = asNullableString(rawBillingContact?.email);
  const technicalContactName = asNullableString(rawTechnicalContact?.name);
  const technicalContactEmail = asNullableString(rawTechnicalContact?.email);

  return {
    tenantId: asNumber(raw.tenantId ?? raw.id),
    tenantName: asString(raw.tenantName ?? raw.name),
    statusId: statusId,
    statusName: asNullableString(raw.statusName ?? raw.StatusName ?? raw.status),
    canUse: asBoolean(raw.canUse ?? raw.CanUse),
    code: asNullableString(raw.code ?? raw.tenantCode),
    shardId,
    shardName: asNullableString(raw.shardName ?? raw.ShardName ?? raw.shardDatabase ?? raw.ShardDatabase),
    addressLine1,
    addressLine2,
    suburb: asNullableString(raw.suburb),
    cityId: asNullableNumber(raw.cityId ?? raw.CityId),
    city,
    countryId: asNullableNumber(raw.countryId ?? raw.CountryId),
    region: normalizedRegion,
    country: asNullableString(raw.country ?? raw.countryName ?? raw.CountryName),
    billingContact: {
      name: billingContactName,
      email: billingContactEmail
    },
    technicalContact: {
      name: technicalContactName,
      email: technicalContactEmail
    },
    settings: normalizeSettings(raw.settings ?? raw.Settings)
  };
}

function normalizeTenantList(data: unknown): Tenant[] {
  if (Array.isArray(data)) {
    return data.map((item) => normalizeTenant((item ?? {}) as TenantApiShape));
  }

  if (data && typeof data === 'object') {
    const objectData = data as Record<string, unknown>;
    if (Array.isArray(objectData.items)) {
      return objectData.items.map((item) => normalizeTenant((item ?? {}) as TenantApiShape));
    }
  }

  return [];
}

function toApiPayload(data: UpsertTenantRequest): Record<string, unknown> {
  const addressLine1 = asNullableString(data.addressLine1);
  const addressLine2 = asNullableString(data.addressLine2);
  const building = addressLine2 ? addressLine1 : null;
  const street = addressLine2 ?? addressLine1;
  const region = asNullableString(data.region);
  const billingContact = {
    name: asNullableString(data.billingContact?.name),
    email: asNullableString(data.billingContact?.email)
  };
  const technicalContact = {
    name: asNullableString(data.technicalContact?.name),
    email: asNullableString(data.technicalContact?.email)
  };

  return {
    tenantName: data.tenantName,
    statusId: data.statusId,
    statusName: asNullableString(data.statusName),
    code: asNullableString(data.code),
    shardId: asNullableIdString(data.shardId),
    shardName: asNullableString(data.shardName),
    addressLine1,
    address1: addressLine1,
    addressLine2,
    address2: addressLine2,
    building,
    street,
    suburb: asNullableString(data.suburb),
    cityId: data.cityId,
    city: asNullableString(data.city),
    countryId: data.countryId,
    region,
    state: region,
    country: asNullableString(data.country),
    billingContact,
    technicalContact,
    settings: data.settings
  };
}

const tenantsCacheKey = (canUse: boolean | null) => `${endpoints.key}:${String(canUse)}`;

export async function getTenants(canUse: boolean | null = true): Promise<Tenant[]> {
  const query = new URLSearchParams();
  if (canUse !== null) query.set('canUse', String(canUse));
  const suffix = query.toString();
  const url = suffix ? `${endpoints.key}?${suffix}` : endpoints.key;
  const response = await axiosServices.get(url);
  return normalizeTenantList(response.data);
}

// Shared client-side cache for the tenant list/dropdown so every consumer (header tenant
// switcher, tenant management screen, etc.) reads and revalidates the same cached entry.
export function useTenants(canUse: boolean | null = true) {
  const cacheKey = tenantsCacheKey(canUse);
  const { data, isLoading, error } = useSWR<Tenant[]>(cacheKey, () => getTenants(canUse), {
    revalidateOnFocus: false
  });

  return useMemo(
    () => ({
      tenants: data ?? [],
      tenantsLoading: isLoading,
      // Distinguishes "the list came back empty" from "there is no list yet". Callers act on an
      // empty list (the user has no tenants), which must not be inferred from an in-flight fetch.
      tenantsLoaded: data !== undefined,
      tenantsError: error
    }),
    [data, error, isLoading]
  );
}

// Clears the client-side tenant list cache so the next read re-fetches from the API.
export function revalidateTenants() {
  return mutate((key: unknown) => typeof key === 'string' && key.startsWith(endpoints.key));
}

export async function createTenant(data: UpsertTenantRequest): Promise<Tenant> {
  const response = await axiosServices.post(endpoints.key, toApiPayload(data));
  const created = normalizeTenant((response.data ?? {}) as TenantApiShape);
  await revalidateTenants();
  return created;
}

export async function updateTenant(tenantId: number, data: UpsertTenantRequest): Promise<Tenant> {
  const response = await axiosServices.put(`${endpoints.key}/${tenantId}`, toApiPayload(data));
  const updated = normalizeTenant((response.data ?? {}) as TenantApiShape);
  await revalidateTenants();
  return updated;
}

// ==============================|| TENANT HISTORY ||============================== //

export interface TenantHistoryEntry {
  tenantHxId: number;
  tenantId: number;
  statusId: number;
  statusName: string | null;
  notes: string;
  changeDate: string | null;
  changeBy: number;
  changeByName: string | null;
}

/** Changes made to a tenant, newest first. */
export async function getTenantHistory(tenantId: number): Promise<TenantHistoryEntry[]> {
  const response = await axiosServices.get<TenantHistoryEntry[]>(`${endpoints.key}/${tenantId}/history`);
  return Array.isArray(response.data) ? response.data : [];
}
